import { ActionBar } from '@/modules/application';
import { Card, CardContent, Typography, TextField, Button } from '@mui/material';
import { FunctionComponent, useState } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { AssetTypeFormInput, AssetTypeFormModel } from './AssetTypeFormModel';
import { ActionBarDeleteButton } from '@/modules/application/components/ActionBarDeleteButton';
import { assetTypeService } from '../../api/asset-types/asset-type.service';
import { useNavigate, useParams } from 'react-router';
import { isFinite, isNil } from 'lodash-es';
import { ApiResponseCode } from '@/lib/api/api-response-code';
import { toast } from 'react-toastify';
import ImageUpload from '@/components/inputs/ImageUpload';
import { useUser } from '@/modules/users/contexts/UserContext';
import { Permission } from '@/modules/users/submodules/roles/api/permissions.contracts';
import { useTranslation } from '@/lib';
import MoveAssetsToAnotherTypeDialog from '../MoveAssetsToAnotherTypeDialog';
import { AssetTypeModel } from '../../types/AssetTypeModel';

interface AssetTypeFormDisplayProps {
  form: UseFormReturn<AssetTypeFormInput, unknown, AssetTypeFormModel>;
  onSubmit: (data: AssetTypeFormModel) => void;
  isCreate?: boolean;
  assetType?: AssetTypeModel;
}

const AssetTypeFormDisplay: FunctionComponent<AssetTypeFormDisplayProps> = ({ form, onSubmit, isCreate = false, assetType }) => {
  const { formState, register, handleSubmit, getValues, control, watch } = form;
  const { errors, isSubmitting } = formState;
  const [isDeleting, setIsDeleting] = useState(false);
  const hasAssetTypeEditPermission = useUser().hasPermission(Permission.ASSET_TYPES_EDIT);
  const { assetTypeId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isMoveAssetsDialogOpen, setIsMoveAssetsDialogOpen] = useState(false);

  const onDeleteConfirmed = async () => {
    if (isNil(assetTypeId)) return;

    const assetTypeIdNumber = Number.parseInt(assetTypeId);

    if (!isFinite(assetTypeIdNumber)) return;

    setIsDeleting(true);

    const deleteAssetTypeResponse = await assetTypeService.delete(assetTypeIdNumber);

    if (deleteAssetTypeResponse.isSuccess) {
      toast.success(t('asset_type_deleted'));
      navigate('../');
    } else {
      if (deleteAssetTypeResponse.responseCode === ApiResponseCode.ASSET_TYPE_HAS_ASSETS) {
        setIsMoveAssetsDialogOpen(true);
      } else {
        toast.error(t('error_deleting_asset_type'));
      }
    }
    setIsDeleting(false);
  };

  function onImageUploaded(file: File) {
    console.log(file);
    form.setValue('image', file);
  }

  function onImageRemoved() {
    console.log('Image removed');
    form.setValue('image', null);
  }

  const handleAssetsMoved = async () => {
    await onDeleteConfirmed();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="grid gap-4 lg:grid-cols-2 2xl:grid-cols-3 ">
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" className="pb-2" color="textSecondary">
              {t('details')}
            </Typography>
            <div className="flex w-full flex-col gap-y-2">
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField error={errors.name !== undefined} helperText={errors.name?.message} label={t('name')} {...field} />
                )}
              />

              <Controller
                name="code"
                control={control}
                render={({ field }) => (
                  <TextField error={errors.code !== undefined} helperText={errors.code?.message} label={t('code')} {...field} />
                )}
              />

              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    error={errors.description !== undefined}
                    helperText={errors.description?.message}
                    label={t('description')}
                    {...field}
                  />
                )}
              />

              <Controller
                name="externalId"
                control={control}
                render={({ field }) => (
                  <TextField
                    error={errors.externalId !== undefined}
                    helperText={errors.externalId?.message}
                    label={t('external_id')}
                    {...field}
                  />
                )}
              />

              <Controller
                name="widthInMm"
                control={control}
                render={({ field }) => (
                  <TextField
                    error={errors.widthInMm !== undefined}
                    helperText={errors.widthInMm?.message}
                    label={t('width_mm')}
                    type="number"
                    {...field}
                  />
                )}
              />

              <Controller
                name="heightInMm"
                control={control}
                render={({ field }) => (
                  <TextField
                    error={errors.heightInMm !== undefined}
                    helperText={errors.heightInMm?.message}
                    label={t('height_mm')}
                    type="number"
                    {...field}
                  />
                )}
              />

              <Controller
                name="depthInMm"
                control={control}
                render={({ field }) => (
                  <TextField
                    error={errors.depthInMm !== undefined}
                    helperText={errors.depthInMm?.message}
                    label={t('depth_mm')}
                    type="number"
                    {...field}
                  />
                )}
              />

              <Controller
                name="volumeInCubicMm"
                control={control}
                render={({ field }) => (
                  <TextField
                    error={errors.volumeInCubicMm !== undefined}
                    helperText={errors.volumeInCubicMm?.message}
                    label={t('volume_cubic_mm')}
                    type="number"
                    {...field}
                  />
                )}
              />

              <Controller
                name="productCapacity"
                control={control}
                render={({ field }) => (
                  <TextField
                    error={errors.productCapacity !== undefined}
                    helperText={errors.productCapacity?.message}
                    label={t('product_capacity')}
                    type="number"
                    {...field}
                    value={field.value ?? ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      field.onChange(value === '' ? undefined : value);
                    }}
                  />
                )}
              />

              <Controller
                name="modelYear"
                control={control}
                render={({ field }) => (
                  <TextField
                    error={errors.modelYear !== undefined}
                    helperText={errors.modelYear?.message}
                    label={t('model_year')}
                    type="number"
                    {...field}
                  />
                )}
              />
              <Controller
                name="monetaryValue"
                control={control}
                render={({ field }) => (
                  <TextField
                    error={errors.monetaryValue !== undefined}
                    helperText={errors.monetaryValue?.message}
                    label={t('monetary_value')}
                    type="number"
                    {...field}
                  />
                )}
              />
            </div>
          </CardContent>
        </Card>
        <div>
          <Card variant="outlined" className="self-start">
            <CardContent>
              <Typography variant="h6" className="pb-2" color="textSecondary">
                {t('image')}
              </Typography>
              <ImageUpload
                onImageUpload={onImageUploaded}
                onRemoveImage={onImageRemoved}
                initialImageUrl={form.getValues('imageThumbnailUrl') ?? undefined}
              />
              {errors.image && <div className="text-red-500">{errors.image.message}</div>}
            </CardContent>
          </Card>
          {!isCreate && assetType && (
            <Card variant="outlined" className="mt-4">
              <CardContent>
                <Typography variant="h6" className="pb-2" color="textSecondary">
                  {t('overview')}
                </Typography>
                <div className="grid grid-cols-2 gap-y-3">
                  <span className="font-semibold">{t('number_of_assets')}:</span>
                  <span>{assetType.count}</span>

                  <span className="font-semibold">{t('total_monetary_value')}:</span>
                  <span>{assetType.totalAssetValue ?? 0}</span>

                  <span className="font-semibold">{t('total_product_capacity')}:</span>
                  <span>{assetType.totalProductCapacity ?? 0}</span>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
      <ActionBar visible={hasAssetTypeEditPermission}>
        <Button variant="contained" type="submit" loading={isSubmitting}>
          {t('save')}
        </Button>
        <ActionBarDeleteButton
          deleteConfirmationQuestion={t('delete_asset_type_confirmation')}
          isVisible={!isCreate}
          isLoading={isDeleting}
          onDeleteConfirmed={onDeleteConfirmed}
        />
      </ActionBar>
      <MoveAssetsToAnotherTypeDialog
        isOpen={isMoveAssetsDialogOpen}
        onClose={() => setIsMoveAssetsDialogOpen(false)}
        onAssetsMoved={handleAssetsMoved}
        assetTypeToDeleteID={assetTypeId ? Number.parseInt(assetTypeId) : 0}
        assetTypeToDeleteName={getValues('name')}
      />
    </form>
  );
};

export default AssetTypeFormDisplay;
